import { motion } from 'framer-motion';
import { CheckCircle, MapPin, Phone } from 'lucide-react';
import React from 'react';

// Location interface to match your application structure
export interface Location {
  id: string;
  city: string;
  address: string;
  phone: string;
  country: string;
  zip: string;
  timeZone: string;
  lat: string;
  long: string;
}

// Define default colors to use if not provided
const defaultColors = {
  cardBackground: '#1a1a1a',
  cardBorder: '#DDB88919',
  cardBorderSelected: '#22c55e49',
  cardShadow: 'rgba(221,184,137,0.07)',
  cardShadowSelected: 'rgba(34,197,94,0.25)',
  titleText: '#DDB889',
  subtitleText: '#9CA3AF',
  infoText: '#D1D5DB',
  iconColor: '#DDB889B2',
  selectionBackground: '#DDB889',
  selectionIcon: '#000000',
  mapBackground: '#00000066',
  mapPinColor: '#DDB88966'
};

// Define animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

const locationVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15
    }
  }
};

interface LocationListProps {
  locations: { node: Location }[];
  colors?: {
    cardBackground?: string;
    cardBorder?: string;
    cardBorderSelected?: string;
    cardShadow?: string;
    cardShadowSelected?: string;
    titleText?: string;
    subtitleText?: string;
    infoText?: string;
    iconColor?: string;
    selectionBackground?: string;
    selectionIcon?: string;
    mapBackground?: string;
    mapPinColor?: string;
  };
  isClickable?: boolean;
  onLocationClick?: (location: Location) => void;
  selectedLocations?: Location | Location[] | null;
}

const LocationList: React.FC<LocationListProps> = ({
  locations = [],
  colors = {},
  isClickable = false,
  onLocationClick = () => {},
  selectedLocations = null,
}) => {
  // Merge provided colors with defaults
  const themeColors = { ...defaultColors, ...colors };

  // Check if a location is selected
  const isLocationSelected = (location: Location) => {
    if (!selectedLocations) return false;
    if (Array.isArray(selectedLocations)) {
      return selectedLocations.some(selected => selected.id === location.id);
    }
    return selectedLocations.id === location.id;
  };

  // Handle location selection
  const handleLocationClick = (location: Location) => {
    if (!isClickable || !onLocationClick) return;
    onLocationClick(location);
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="lp-w-full"
    >
      {locations.map(({ node: location }) => (
        <motion.div
          key={location.id}
          variants={locationVariants}
          className="lp-flex lp-items-center lp-gap-3 lp-p-3 lp-rounded-xl lp-border lp-mb-2 lp-transition-all lp-duration-300"
          style={{
            backgroundColor: themeColors.cardBackground,
            borderColor: isLocationSelected(location)
              ? themeColors.cardBorderSelected
              : themeColors.cardBorder,
            boxShadow: isLocationSelected(location)
              ? themeColors.cardShadowSelected
              : themeColors.cardShadow,
            cursor: isClickable ? 'pointer' : 'default'
          }}
          onClick={() => handleLocationClick(location)}
          whileHover={isClickable ? { scale: 1.01 } : {}}
        >
          {/* Selection/Location Icon */}
          <div className="lp-flex-shrink-0">
            <motion.div
              className="lp-w-10 lp-h-10 lp-rounded-full lp-flex lp-items-center lp-justify-center"
              style={{
                backgroundColor: isLocationSelected(location)
                  ? themeColors.selectionBackground
                  : `${themeColors.cardBackground}CC`
              }}
              whileHover={{ rotate: isLocationSelected(location) ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              {isLocationSelected(location) ? (
                <CheckCircle
                  className="lp-w-5 lp-h-5"
                  style={{ color: themeColors.selectionIcon }}
                />
              ) : (
                <MapPin
                  className="lp-w-5 lp-h-5"
                  style={{ color: themeColors.titleText }}
                />
              )}
            </motion.div>
          </div>

          {/* Location Details */}
          <div className="lp-flex-grow lp-min-w-0">
            <h4
              className="lp-text-base lp-font-medium lp-truncate"
              style={{ color: themeColors.titleText }}
            >
              {location.city}
            </h4>
            <p
              className="lp-text-xs lp-truncate"
              style={{ color: themeColors.subtitleText }}
            >
              {location.address}
            </p>
          </div>

          {/* Phone Information */}
          <div className="lp-flex-shrink-0 lp-flex lp-items-center lp-gap-1">
            <Phone
              className="lp-w-3 lp-h-3"
              style={{ color: themeColors.iconColor }}
            />
            <span
              className="lp-text-xs"
              style={{ color: themeColors.infoText }}
            >
              {location.phone}
            </span>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
};

export default LocationList;
